import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { NotificationService, Notification, NotificationPreferences } from '@/lib/notifications';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  loading: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

// Map a per-user team notification row into the shared Notification shape.
const mapTeam = (r: any): Notification => ({
  id: r.id, title: r.title, message: r.message || '', type: r.type || 'team',
  target_region: null, is_active: true, created_at: r.created_at,
  created_by_admin: true, read: !!r.read, user_id: r.user_id,
});

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  // ids that came from the per-user user_notifications table (server-side read state)
  const teamIds = useRef<Set<string>>(new Set());

  const refreshNotifications = async () => {
    try {
      setLoading(true);
      const broadcast = await NotificationService.getUserNotifications();
      const prefs = NotificationService.getUserPreferences();

      // Per-user team notifications (best-effort — never block the broadcast list).
      let team: Notification[] = [];
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data } = await supabase
            .from('user_notifications')
            .select('id, title, message, type, read, user_id, created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(50);
          team = (data || []).map(mapTeam);
          teamIds.current = new Set(team.map((t) => t.id));
        }
      } catch (e) { /* team notifications optional */ }

      const all = [...team, ...broadcast].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      setNotifications(all);
      setUnreadCount(NotificationService.getUnreadCount(all));
      setPreferences(prefs);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    if (teamIds.current.has(id)) {
      try { await supabase.from('user_notifications').update({ read: true }).eq('id', id); } catch { /* ignore */ }
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return;
    }
    const success = NotificationService.markAsRead(id);
    if (success) {
      setNotifications((prev) => prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = async () => {
    // Server-side team notifications
    const unreadTeam = notifications.filter((n) => teamIds.current.has(n.id) && !n.read);
    if (unreadTeam.length) {
      try { await supabase.from('user_notifications').update({ read: true }).in('id', unreadTeam.map((n) => n.id)); } catch { /* ignore */ }
    }
    // Local broadcast notifications
    NotificationService.markAllAsRead(notifications.filter((n) => !teamIds.current.has(n.id)));
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
    setUnreadCount(0);
  };

  const updatePreferences = (prefs: Partial<NotificationPreferences>) => {
    const success = NotificationService.updatePreferences(prefs);
    if (success) {
      setPreferences((prev) => (prev ? { ...prev, ...prefs } : null));
      toast({ title: 'Preferences Updated', description: 'Your notification preferences have been saved.' });
    } else {
      toast({ title: 'Error', description: 'Failed to update notification preferences.', variant: 'destructive' });
    }
  };

  useEffect(() => {
    refreshNotifications();

    // Realtime: existing broadcast notifications.
    const subscription = NotificationService.subscribeToRealtime((newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);
      toast({ title: newNotification.title, description: newNotification.message });
    });

    // Realtime: per-user team notifications.
    let teamChannel: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        teamChannel = supabase
          .channel(`user_notifications_${user.id}`)
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_notifications', filter: `user_id=eq.${user.id}` }, (payload) => {
            const n = mapTeam(payload.new);
            teamIds.current.add(n.id);
            setNotifications((prev) => [n, ...prev]);
            setUnreadCount((prev) => prev + 1);
            toast({ title: n.title, description: n.message });
          })
          .subscribe();
      } catch { /* ignore */ }
    })();

    return () => {
      subscription.unsubscribe();
      if (teamChannel) supabase.removeChannel(teamChannel);
    };
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    preferences,
    loading,
    markAsRead,
    markAllAsRead,
    updatePreferences,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
